/**
 * Factuurgegevens-blok voor /account.
 * Slaat organisatienaam, adres, KvK en BTW op via PUT /api/account/billing.
 * Deze gegevens komen automatisch terug op elke nieuwe factuur (PDF).
 */
import { useCallback, useEffect, useState } from "react";
import { Building2, Loader2, Save, Hash, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  organization_name: "",
  address: "",
  postal_code: "",
  city: "",
  country: "Nederland",
  kvk: "",
  vat_number: "",
  invoice_email: "",
};

function Field({ label, value, onChange, testid, placeholder, hint }) {
  return (
    <div>
      <label className="stat-label">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
      />
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function CustomerNumberChip({ number }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(number); toast.success("Klantnummer gekopieerd."); }
    catch { toast.error("Kopiëren mislukt."); }
  };
  return (
    <div className="border-2 border-foreground bg-muted/30 px-3 py-2 min-w-[190px]" data-testid="billing-customer-number-box">
      <div className="stat-label flex items-center gap-1"><Hash className="w-3 h-3" /> Klantnummer</div>
      {number ? (
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-mono-num font-bold text-lg tracking-wider" data-testid="billing-customer-number">{number}</span>
          <button onClick={copy} data-testid="billing-customer-number-copy" title="Kopieer klantnummer"
            className="p-1 border-2 border-foreground bg-background hover:bg-foreground hover:text-background transition-colors">
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground mt-0.5" data-testid="billing-customer-number-pending">Wordt toegekend bij je eerste factuur</div>
      )}
      <div className="text-[11px] text-muted-foreground mt-1">Noem dit nummer bij vragen over facturen.</div>
    </div>
  );
}

export default function BillingDetailsBlock() {
  const [data, setData] = useState(null);
  const [customerNumber, setCustomerNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/billing");
      const { customer_number, ...rest } = data;
      setCustomerNumber(customer_number || null);
      setData({ ...EMPTY, ...rest });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon factuurgegevens niet ophalen.");
      setData({ ...EMPTY });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/account/billing", data);
      setData({ ...EMPTY, ...saved.billing_details });
      toast.success("Factuurgegevens opgeslagen. Nieuwe facturen gebruiken deze gegevens automatisch.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const patch = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  if (loading) {
    return (
      <div className="border-2 border-foreground bg-card heavy-shadow p-6 md:p-8" data-testid="billing-loading">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="border-2 border-foreground bg-card heavy-shadow p-6 md:p-8" data-testid="billing-card">
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 stat-label">
            <Building2 className="w-3.5 h-3.5" /> Factuurgegevens
          </div>
          <h2 className="font-display font-black text-2xl uppercase mt-1">Voor op de factuur</h2>
        </div>
        <CustomerNumberChip number={customerNumber} />
      </div>
      <div className="mb-4">
        <p className="text-xs text-muted-foreground mt-1">
          Deze gegevens verschijnen op iedere nieuwe factuur. KvK en BTW zijn optioneel (voor niet-BTW-plichtige verenigingen kun je die leeg laten).
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Field
            label="Organisatie / verenigingsnaam"
            value={data.organization_name}
            onChange={(v) => patch("organization_name", v)}
            testid="billing-organization-name"
            placeholder="bijv. Watersportvereniging De Randmeren"
          />
        </div>
        <div className="md:col-span-2">
          <Field
            label="Straat en huisnummer"
            value={data.address}
            onChange={(v) => patch("address", v)}
            testid="billing-address"
            placeholder="Havenweg 1"
          />
        </div>
        <Field
          label="Postcode"
          value={data.postal_code}
          onChange={(v) => patch("postal_code", v)}
          testid="billing-postal-code"
          placeholder="1234 AB"
        />
        <Field
          label="Plaats"
          value={data.city}
          onChange={(v) => patch("city", v)}
          testid="billing-city"
          placeholder="Almere"
        />
        <Field
          label="Land"
          value={data.country}
          onChange={(v) => patch("country", v)}
          testid="billing-country"
          placeholder="Nederland"
        />
        <Field
          label="Factuur-e-mail (optioneel)"
          value={data.invoice_email}
          onChange={(v) => patch("invoice_email", v)}
          testid="billing-invoice-email"
          placeholder="penningmeester@club.nl"
          hint="Laat leeg om je login-e-mailadres te gebruiken."
        />
        <Field
          label="KvK-nummer (optioneel)"
          value={data.kvk}
          onChange={(v) => patch("kvk", v)}
          testid="billing-kvk"
          placeholder="12345678"
        />
        <Field
          label="BTW-nummer (optioneel)"
          value={data.vat_number}
          onChange={(v) => patch("vat_number", v)}
          testid="billing-vat"
          placeholder="NL001234567B01"
        />
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="billing-save-btn"
          className="btn-brutal bg-primary text-primary-foreground px-5 py-3 font-display uppercase font-black text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Opslaan…" : "Factuurgegevens opslaan"}
        </button>
      </div>
    </div>
  );
}
